import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { FormFieldConfig, PossibleFormFieldValues } from "../../interfaces/IForms";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

export default function FormField({
  disabled = false,
  error,
  helperText,
  label,
  onChange,
  options,
  placeholder = "",
  required = false,
  type,
  value,
}: FormFieldConfig<PossibleFormFieldValues>) {
  const minDate = dayjs("1950-01-01");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = type === "number" ? Number(event.target.value) : event.target.value;
    if (onChange) {
      onChange(newValue as PossibleFormFieldValues);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.checked as PossibleFormFieldValues);
    }
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    if (onChange) {
      onChange(newValue ? newValue.toDate() : null);
    }
  };

  return (
    <>
      {type === "date" && (
        <DatePicker
          disabled={disabled}
          format="DD.MM.YYYY"
          label={label}
          onChange={handleDateChange}
          value={value ? dayjs(value as Date) : null}
          slotProps={{
            textField: {
              required: required,
              error: error,
              helperText: helperText,
              placeholder: placeholder,
              fullWidth: true,
              size: "small",
            },
          }}
          minDate={minDate}
          disableFuture
        />
      )}
      {type === "checkbox" && (
        <FormControl error={error} component="fieldset" variant="standard">
          <FormControlLabel
            control={
              <Checkbox
                checked={value as boolean}
                disabled={disabled}
                onChange={handleCheckboxChange}
                required={required}
              />
            }
            label={label}
          />
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      )}
      {type === "select" && options && (
        <FormControl fullWidth required={required} error={error} size="small">
          <InputLabel>{label}</InputLabel>
          <Select
            disabled={disabled}
            onChange={(e) => onChange && onChange(e.target.value as PossibleFormFieldValues)}
            placeholder={placeholder}
            value={value}
          >
            {options.map((option) => {
              // Sprawdź czy opcja to obiekt z value i label
              if (
                typeof option === "object" &&
                option !== null &&
                "value" in option &&
                "label" in option
              ) {
                return (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                );
              }
              // Dla prostych wartości (enum, string, number)
              return (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              );
            })}
          </Select>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      )}
      {type !== "checkbox" && type !== "date" && type !== "select" && (
        <TextField
          disabled={disabled}
          error={error}
          fullWidth
          helperText={helperText}
          label={label}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          size="small"
          type={type}
          value={value}
        />
      )}
    </>
  );
}
